import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface WaybillExportStats {
  scanCount: number;
  totalQty: number;
  employees: string[];
}

// Lấy nhanh số liệu xuất kho cho từng mã vận đơn đang có cảnh báo:
//   - scanCount: số lần quét thực tế (số bản ghi xuất kho).
//   - totalQty : tổng số gói đã xuất của mã đó (gộp mọi lần xuất trùng).
//   - employees: danh sách nhân viên đã thực hiện các lần xuất (để biết ai cần kiểm tra).
// Dùng để hiển thị trong popup cảnh báo xuất trùng.
export function useDuplicateWaybillStats(waybills: string[]) {
  const [stats, setStats] = useState<Map<string, WaybillExportStats>>(new Map());

  // Khóa danh sách mã để tránh gọi lại khi thứ tự thay đổi nhưng nội dung không đổi.
  const key = useMemo(
    () => [...new Set(waybills.filter(Boolean))].sort().join("|"),
    [waybills]
  );

  useEffect(() => {
    const codes = key ? key.split("|") : [];
    if (codes.length === 0) {
      setStats(new Map());
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from("lich_su_xuat_kho")
          .select("ma_van_don, so_luong_xuat, id_nhan_vien, thoi_gian_xuat")
          .in("ma_van_don", codes)
          .order("thoi_gian_xuat", { ascending: true });
        if (error) throw error;

        const rows = data ?? [];

        // Gom số liệu + danh sách id nhân viên (theo thứ tự xuất) cho từng mã.
        const map = new Map<string, WaybillExportStats>();
        const employeeIdsPerWaybill = new Map<string, string[]>();
        const allEmployeeIds = new Set<string>();

        rows.forEach((row) => {
          const maVanDon = row.ma_van_don as string | null;
          if (!maVanDon) return;

          const qty = Number(row.so_luong_xuat) || 0;
          const current = map.get(maVanDon) ?? {
            scanCount: 0,
            totalQty: 0,
            employees: [],
          };
          current.scanCount += 1;
          current.totalQty += qty;
          map.set(maVanDon, current);

          const empId = row.id_nhan_vien as string | null;
          if (empId) {
            allEmployeeIds.add(empId);
            const list = employeeIdsPerWaybill.get(maVanDon) ?? [];
            list.push(empId);
            employeeIdsPerWaybill.set(maVanDon, list);
          }
        });

        // Tra tên nhân viên rồi gán vào từng mã (giữ thứ tự xuất, bỏ trùng lặp).
        if (allEmployeeIds.size > 0) {
          const { data: empData } = await supabase
            .from("nhan_vien")
            .select("id, ho_ten")
            .in("id", [...allEmployeeIds]);

          const nameById = new Map<string, string>();
          (empData ?? []).forEach((e) => {
            nameById.set(e.id as string, (e.ho_ten as string) ?? "");
          });

          employeeIdsPerWaybill.forEach((ids, maVanDon) => {
            const entry = map.get(maVanDon);
            if (!entry) return;
            const names: string[] = [];
            ids.forEach((id) => {
              const name = nameById.get(id);
              if (name && !names.includes(name)) names.push(name);
            });
            entry.employees = names;
          });
        }

        if (!cancelled) setStats(map);
      } catch {
        if (!cancelled) setStats(new Map());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return stats;
}